/**
 * Timestamps the passport store writes are naive wall-clock strings
 * ("2026-09-28 09:14:02") in the app's timezone — `date('Y-m-d H:i:s')` under
 * config('app.timezone'). Reading one as the viewer's local time is off by the
 * difference between the two zones, so "how long ago" is measured wall clock
 * against wall clock: the stored time against the current time in the same
 * zone.
 */

const NAIVE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;

/** A naive timestamp as {y, M, d, h, m, s}, or null when it is not one. */
export function parseWallClock(value) {
    const match = typeof value === 'string' ? NAIVE.exec(value.trim()) : null;
    if (!match) {
        return null;
    }
    const [, y, M, d, h, m, s = '0'] = match;

    return { y: +y, M: +M, d: +d, h: +h, m: +m, s: +s };
}

// Wall-clock parts compared as if they were UTC: only their difference is
// ever used, so the zone they are pinned to does not matter.
const asEpoch = ({ y, M, d, h, m, s }) => Date.UTC(y, M - 1, d, h, m, s);

function nowInZone(timeZone, now = new Date()) {
    try {
        const parts = Object.fromEntries(
            new Intl.DateTimeFormat('en-US', {
                timeZone,
                hourCycle: 'h23',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
            }).formatToParts(now).map(({ type, value }) => [type, value]),
        );

        return { y: +parts.year, M: +parts.month, d: +parts.day, h: +parts.hour, m: +parts.minute, s: +parts.second };
    } catch {
        // An unknown zone: the viewer's own clock is the best guess left.
        return { y: now.getFullYear(), M: now.getMonth() + 1, d: now.getDate(), h: now.getHours(), m: now.getMinutes(), s: now.getSeconds() };
    }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Sep 12" this year, "Sep 12, 2025" otherwise; with `dayFirst`, "12 Sep" and
 * "12 Sep 2025" (the order of dayMonthYear below).
 */
export function shortDate(value, timeZone, now = new Date(), { dayFirst = false } = {}) {
    const at = parseWallClock(value);
    if (!at) {
        return value ?? '';
    }
    const today = nowInZone(timeZone, now);
    const sameYear = at.y === today.y;

    if (dayFirst) {
        return `${at.d} ${MONTHS[at.M - 1]}${sameYear ? '' : ` ${at.y}`}`;
    }

    return `${MONTHS[at.M - 1]} ${at.d}${sameYear ? '' : `, ${at.y}`}`;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date day first, "11 Sep 2026" (with `time`, "11 Sep 2026, 08:15"): the
 * order the app's newer screens write (`d MMM yyyy`), for a component told
 * `dayFirst` where it would otherwise use the browser's locale. A date-only
 * string ("2026-09-11") is that calendar day wherever the reader is; anything
 * else is read as `new Date()` reads it, in the reader's zone, as
 * toLocaleString() would. Returns null for a value that is no date.
 */
export function dayMonthYear(value, { time = false } = {}) {
    const dateOnly = typeof value === 'string' ? DATE_ONLY.exec(value.trim()) : null;
    if (dateOnly) {
        const [, y, M, d] = dateOnly;

        return `${+d} ${MONTHS[+M - 1]} ${+y}`;
    }
    const at = value instanceof Date ? value : new Date(value);
    if (value === null || value === undefined || Number.isNaN(at.getTime())) {
        return null;
    }
    const date = `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}`;

    return time ? `${date}, ${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}` : date;
}

/** "09:14" */
export function clockTime(value) {
    const at = parseWallClock(value);

    return at ? `${String(at.h).padStart(2, '0')}:${String(at.m).padStart(2, '0')}` : (value ?? '');
}

/**
 * "just now", "12m ago", "3h ago", "Yesterday", "Sep 24" — short enough for a
 * list column, with the exact time left to the caller's `title`.
 */
export function relativeTime(value, timeZone, now = new Date(), { dayFirst = false } = {}) {
    const at = parseWallClock(value);
    if (!at) {
        return value ?? '';
    }
    const today = nowInZone(timeZone, now);
    const seconds = (asEpoch(today) - asEpoch(at)) / 1000;

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400 && today.d === at.d) return `${Math.floor(seconds / 3600)}h ago`;

    const yesterday = asEpoch({ ...today, h: 0, m: 0, s: 0 }) - 86400000;
    if (asEpoch({ ...at, h: 0, m: 0, s: 0 }) === yesterday) return 'Yesterday';

    return shortDate(value, timeZone, now, { dayFirst });
}

/**
 * The day heading a changelog entry is grouped under: "Today", "Yesterday",
 * or the short date.
 */
export function dayLabel(value, timeZone, now = new Date(), { dayFirst = false } = {}) {
    const at = parseWallClock(value);
    if (!at) {
        return value ?? '';
    }
    const today = nowInZone(timeZone, now);
    const day = asEpoch({ ...at, h: 0, m: 0, s: 0 });
    const todayStart = asEpoch({ ...today, h: 0, m: 0, s: 0 });

    if (day === todayStart) return 'Today';
    if (day === todayStart - 86400000) return 'Yesterday';

    return shortDate(value, timeZone, now, { dayFirst });
}
